import { Button, Col, Image, Row, Text } from '@opendoor/bricks-next';
import type { EntryComponent } from 'cms/entries/entries';
import { Desc } from 'components/landing-pages-v2/shared/Typography';
import type { ILpComponentMapBackgroundHero } from 'declarations/contentful';

import Grid, { gridStyles } from './Grid';
import Marker, { MarkerPulseClassName } from './shared/Marker';
import { resizeMapBackgroundImageUrl, resizeMapMarkerImageUrl } from './shared/utils';

const MAP_BACKGROUND_IMAGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-07-1689881024134-43912.jpg?preset=square-2048';
const MAP_MARKER_IMAGE_URLS = {
  leftTop:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416264-57298.png?preset=square-2048',
  rightTop:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416269-85419.png?preset=square-2048',
  leftMiddle:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416260-76025.png?preset=square-2048',
  leftBottom:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416240-88834.png?preset=square-2048',
  rightBottom:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416253-95450.png?preset=square-2048',
} as const;

const RenderMapBackgroundHero = (entry: ILpComponentMapBackgroundHero) => {
  const { fields } = entry;

  return (
    <Col
      analyticsName={`cosmos-landing-page-map-background-hero-${fields?.analyticsId}`}
      trackImpression
      position="relative"
      justifyContent="center"
      width="100%"
      $largerThanXS={{ minHeight: '$652x' }}
      $largerThanSM={{ minHeight: '$548x', py: '$16x' }}
      $largerThanMD={{ minHeight: '$848x' }}
      alignItems="center"
      py="$10x"
      mt="$50x"
    >
      <Col zIndex={0} width="100%" height="100%" position="absolute" top={0} left={0} right={0}>
        <Image
          alt="map background hero"
          source={{
            uri: resizeMapBackgroundImageUrl(MAP_BACKGROUND_IMAGE_URL),
          }}
          resizeMode="cover"
          width="100%"
          height="100%"
        />
      </Col>
      <Marker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftTop)}
        left="10%"
        top="17%"
        height="$40x"
        width="$101x"
        $largerThanXS={{ top: '12%' }}
        $largerThanSM={{ top: '12%', height: '$51x', width: '$129x' }}
        className={MarkerPulseClassName}
        animationDelay="2s"
      />
      <Marker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.rightTop)}
        right="7%"
        top="4%"
        height="$40x"
        width="$101x"
        $largerThanSM={{ right: '30%', top: '7%', height: '$51x', width: '$129x' }}
        className={MarkerPulseClassName}
        animationDelay="0.5s"
      />
      <Marker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftMiddle)}
        display="none"
        left="8%"
        top="40%"
        height="$40x"
        width="$101x"
        $largerThanSM={{ display: 'flex', height: '$51x', width: '$129x' }}
        className={MarkerPulseClassName}
        animationDelay="0.5s"
      />
      <Marker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftBottom)}
        left="15%"
        top="80%"
        height="$40x"
        width="$101x"
        $largerThanSM={{ top: '85%', height: '$51x', width: '$129x' }}
        className={MarkerPulseClassName}
      />
      <Marker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.rightBottom)}
        right="4%"
        top="90%"
        height="$40x"
        width="$101x"
        $largerThanXS={{ top: '85%' }}
        $largerThanSM={{ right: '15%', top: '60%', height: '$51x', width: '$129x' }}
        className={MarkerPulseClassName}
        animationDelay="2.5s"
      />
      <Grid justifyContent="center" position="relative">
        <Col
          $platform-web={{ gridColumn: gridStyles.colSpan12 }}
          justifyContent="center"
          zIndex={1}
          alignItems="center"
        >
          <Col mb="$12x">
            <Text
              tag="h2"
              typography="$headerSmall"
              display="block"
              color="$contentInversePrimary"
              textAlign="center"
              $largerThanSM={{ typography: '$headerMedium' }}
              $largerThanMD={{ typography: '$headerLarge' }}
            >
              {fields?.title}
            </Text>
            <Text
              tag="h2"
              typography="$headerSmall"
              display="block"
              color="$contentInverseSecondary"
              textAlign="center"
              $largerThanSM={{ typography: '$headerMedium' }}
              $largerThanMD={{ typography: '$headerLarge' }}
              opacity={0.65}
            >
              {fields?.secondaryTitle}
            </Text>
          </Col>
          <Row
            width="100%"
            $largerThanXS={{ maxWidth: '$272x' }}
            $largerThanMD={{ maxWidth: '$375x' }}
            justifyContent="center"
            mb="$24x"
          >
            <Desc
              textAlign="center"
              typography="$bodyMedium"
              $largerThanMD={{ typography: '$bodyXlarge' }}
              color={'$contentInversePrimary'}
            >
              {fields?.description}
            </Desc>
          </Row>
          {fields?.ctaLink && fields?.ctaDisplayText && (
            <Button
              variant="primary"
              aria-label={fields?.ctaDisplayText}
              analyticsName={`cosmos-landing-page-map-background-hero-cta-${fields?.analyticsId}`}
              href={fields?.ctaLink}
              label={fields?.ctaDisplayText}
              size="small"
              maxWidth="$165x"
              height="$60x"
              borderRadius="$7x"
              py="$10x"
              px="$12dot5x"
              backgroundColor="$backgroundAccentBrand"
            />
          )}
        </Col>
      </Grid>
    </Col>
  );
};

const MapBackgroundHero: EntryComponent<ILpComponentMapBackgroundHero> = {
  render: RenderMapBackgroundHero,
};

export default MapBackgroundHero;
